import React, { useState } from "react";
import { Grid } from "@material-ui/core";

import Api from "../../services/api";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import swal from "sweetalert";

const image_formats = ["image/jpg", "image/jpeg", "image/png", "image/webp"];

// Regra do upload em um lugar só, para o texto exibido e a validação nunca divergirem.
const MAX_FILE_MB = 2;
const MAX_FILE_BYTES = MAX_FILE_MB * 1000000;
const ACCEPT_ATTR = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";
const UPLOAD_HINT = `Formatos aceitos: PNG, JPG, JPEG ou WebP — no máximo ${MAX_FILE_MB} MB.`;

// O site mostra a imagem inteira, sem corte: a proporção enviada é a que aparece.
const MEDIDAS = {
  topo: {
    imagem: "Sugestão: 1600 × 400 px (retangular largo).",
    imagem_mobile: "Sugestão: 800 × 600 px.",
  },
  cards: {
    imagem: "Sugestão: 1200 × 1200 px (quadrado) ou 1600 × 900 px (retangular).",
    imagem_mobile: "Sugestão: 1200 × 1200 px (quadrado) ou 1200 × 900 px (retangular).",
  },
};

const dicaProporcao = (section, campo) => {
  const medida = (MEDIDAS[section] || MEDIDAS.cards)[campo];
  const extra =
    section === "cards"
      ? " Use a mesma proporção nos dois cards para eles ficarem iguais lado a lado."
      : "";

  return `A imagem aparece inteira, sem corte: a proporção que você enviar é a que vai aparecer no site. ${medida}${extra}`;
};
const ERRO_TAMANHO = `Imagem acima de ${MAX_FILE_MB} MB. Selecione uma imagem menor.`;
const ERRO_FORMATO = "Formato inválido. Use PNG, JPG, JPEG ou WebP.";

const fileTest = (file) => {
  if (file && file.length === 1) {
    return file[0].size <= MAX_FILE_BYTES;
  }
  return true;
};
const formatTest = (file) => {
  if (file && file.length === 1) {
    return image_formats.includes(file[0].type);
  }
  return true;
};

const validationSchema = yup.object().shape({
  imagem: yup
    .mixed()
    .test("fileSize", ERRO_TAMANHO, fileTest)
    .test("FILE_FORMAT", ERRO_FORMATO, formatTest),
  imagem_mobile: yup
    .mixed()
    .test("fileSize", ERRO_TAMANHO, fileTest)
    .test("FILE_FORMAT", ERRO_FORMATO, formatTest),
  link: yup.string().nullable(),
});

const DESTINO_LABELS = {
  ilhabela: "Ilhabela / SP",
  paraty: "Paraty e Ilha Grande / RJ",
  main: "Banner Principal",
};

const SECTION_LABELS = {
  cards: "Card de Destino",
  topo: "Banner do Topo",
};

const IDIOMA_LABELS = {
  pt: "Português",
  en: "Inglês",
  es: "Espanhol",
};

export default function EditDestino({ data, loadDestinos, setOpenModalEdit }) {
  const [loading, setLoading] = useState(false);
  // Imagens marcadas para remoção: só saem de verdade quando o formulário é salvo.
  const [removerImagem, setRemoverImagem] = useState(false);
  const [removerImagemMobile, setRemoverImagemMobile] = useState(false);
  const Loader = () => <div className="loading"></div>;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const isTopo = data.section === "topo";

  const campoImagem = register("imagem");
  const campoImagemMobile = register("imagem_mobile");

  const onSubmit = (formData) => {
    setLoading(true);
    const imagefile = document.querySelector("#imagem");
    const imagefileMobile = document.querySelector("#imagem_mobile");

    const bodyFormData = new FormData();
    bodyFormData.append("id", data.id);
    bodyFormData.append("link", formData.link || "");
    bodyFormData.append("ordem", formData.ordem != null ? formData.ordem : data.ordem || 0);
    if (imagefile.files.length > 0) {
      bodyFormData.append("imagem", imagefile.files[0]);
    }
    if (imagefileMobile.files.length > 0) {
      bodyFormData.append("imagem_mobile", imagefileMobile.files[0]);
    }
    if (removerImagem) {
      bodyFormData.append("remover_imagem", "1");
    }
    if (removerImagemMobile) {
      bodyFormData.append("remover_imagem_mobile", "1");
    }
    bodyFormData.append("oldimage", data.imagem || "");
    bodyFormData.append("oldimage_mobile", data.imagem_mobile || "");

    const axiosConfig = {
      headers: { "Content-Type": "multipart/form-data" },
    };

    Api.post("/destinoshome/edit", bodyFormData, axiosConfig)
      .then((response) => {
        if (response.data.status === "success") {
          swal("Sucesso!", response.data.message, "success");
          loadDestinos();
          setOpenModalEdit(false);
        } else {
          swal("Erro!", response.data.message, "error");
        }
        setLoading(false);
      })
      .catch((err) => {
        swal("Erro!", "ops! ocorreu um erro: " + err, "error");
        setLoading(false);
      });
  };

  // Prévia da imagem atual + ação de remover. Sem imagem gravada, não mostra nada.
  const renderImagem = (path, marcada, setMarcada) => {
    if (!path) return null;

    return (
      <>
        <img
          src={process.env.REACT_APP_FILES_URL + path}
          className={marcada ? "imagem-removida" : ""}
          style={{ maxWidth: "100%", marginTop: 8 }}
          alt=""
        />
        {marcada ? (
          <>
            <span className="hint">
              Marcada para remoção — será apagada ao salvar.
            </span>
            <button
              type="button"
              className="btn-imagem desfazer"
              onClick={() => setMarcada(false)}
            >
              Desfazer
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-imagem"
            onClick={() => setMarcada(true)}
          >
            Remover imagem
          </button>
        )}
      </>
    );
  };

  // Escolher um arquivo novo cancela a remoção: substituir sempre ganha de remover.
  const aoEscolherArquivo = (campo, setMarcada) => (e) => {
    campo.onChange(e);
    if (e.target.files.length > 0) setMarcada(false);
  };

  return (
    <>
      {loading ? <Loader /> : null}
      <h2>
        Editar {SECTION_LABELS[data.section] || data.section} —{" "}
        {DESTINO_LABELS[data.destino] || data.destino} (
        {IDIOMA_LABELS[data.idioma] || data.idioma})
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className="form">
          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Imagem (desktop)</label>
            <span className="hint">{UPLOAD_HINT}</span>
            <span className="hint">{dicaProporcao(data.section, "imagem")}</span>
            <input
              type="file"
              name="imagem"
              id="imagem"
              accept={ACCEPT_ATTR}
              {...campoImagem}
              onChange={aoEscolherArquivo(campoImagem, setRemoverImagem)}
            />
            {renderImagem(data.imagem, removerImagem, setRemoverImagem)}
            <span className="error">{errors.imagem?.message}</span>
          </Grid>
          <Grid item lg={12} md={12} sm={12} sx={12} className="field">
            <label>Imagem (mobile — opcional)</label>
            <span className="hint">{UPLOAD_HINT}</span>
            <span className="hint">
              {dicaProporcao(data.section, "imagem_mobile")}
            </span>
            <input
              type="file"
              name="imagem_mobile"
              id="imagem_mobile"
              accept={ACCEPT_ATTR}
              {...campoImagemMobile}
              onChange={aoEscolherArquivo(campoImagemMobile, setRemoverImagemMobile)}
            />
            {renderImagem(data.imagem_mobile, removerImagemMobile, setRemoverImagemMobile)}
            <span className="error">{errors.imagem_mobile?.message}</span>
          </Grid>
          {isTopo && (
            <>
              <Grid item lg={12} md={12} sm={12} sx={12} className="field">
                <label>
                  Link (destino do clique — use URL completa para link externo)
                </label>
                <input
                  type="text"
                  {...register("link")}
                  defaultValue={data.link || ""}
                  placeholder="https://..."
                />
                <span className="error">{errors.link?.message}</span>
              </Grid>
              <Grid item lg={12} md={12} sm={12} sx={12} className="field">
                <label>Ordem (menor aparece primeiro no carrossel)</label>
                <input
                  type="number"
                  {...register("ordem")}
                  defaultValue={data.ordem || 0}
                />
                <span className="error">{errors.ordem?.message}</span>
              </Grid>
            </>
          )}
        </Grid>
        <div className="actions">
          <button type="submit" className="save">
            Salvar
          </button>
          <a
            href="#"
            className="cancel"
            onClick={(e) => {
              e.preventDefault();
              setOpenModalEdit(false);
            }}
          >
            Cancelar
          </a>
        </div>
      </form>
    </>
  );
}
